import { createSignal, For, JSXElement, Show, Signal, ValidComponent } from "solid-js"
import { SelectTrigger } from "./fragments/SelectTrigger";
import { SelectGroup } from "./fragments/SelectGroup";
import { SelectItem } from "./fragments/SelectItem";
import { SelectContext, SelectProvider, requireSelectContext } from "./SelectContext";
import { Dynamic } from "solid-js/web";
import Dropdown from '../st-dropdown/st-dropdown';
import styles from './st-select.module.css';
import Flexbox from "../st-flexbox/st-flexbox";

export class SelectProps<T extends string = string> {
    children: JSXElement;
    value!: Signal<T>;
    label?: string;
    icon?: string;
    open?: Signal<boolean>;
    as?: ValidComponent;
    required?: boolean;
    displayValue?: JSXElement
}

export type SelectOption<T> = {
    value: T
    label: string
}



export default function Select<T extends string = string>(props: SelectProps<T>) {
    const ctx = new SelectContext<T>(props.value, props.open ?? createSignal(false));

    return (
        <SelectProvider value={ctx as unknown as SelectContext}>
            <SelectInternal {...props} />
        </SelectProvider>
    );

}

Select.defaultProps = new SelectProps<string>();

// We have to do this because we need the context available here.
function SelectInternal<T extends string = string>(props: SelectProps<T>) {
    const ctx = requireSelectContext<T>();

    return <>
        <Dropdown open={[ctx.open, ctx.setOpen]}>
            {props.children}
            <Show when={!ctx.hasTrigger}>
                <Dropdown.Trigger as={Flexbox} dir="horizontal" middle class={styles['select-trigger']} data-required={props.required} data-empty={!ctx.value()} role='button' tabindex="0">
                    <Flexbox.Item auto>
                        {props.displayValue ?? ctx.defaultDisplayValue()}
                    </Flexbox.Item>
                    <Flexbox.Item>
                        <i class={`fa fa-${props.icon ?? 'caret-down'}`} style='padding-right: 0.25em; opacity: 0.7;'></i>
                    </Flexbox.Item>
                </Dropdown.Trigger>
            </Show>
            <Dropdown.Content>
                <Dynamic component={props.as ?? 'ul'} class={styles['select-list']}>
                    <For each={ctx.groups()}>{group =>
                        <>
                            <Show when={group.label}>
                                <li class={styles['group-label']}>{group.label}</li>
                            </Show>
                            <For each={group.items}>{it => it}</For>
                        </>
                    }</For>
                </Dynamic>
            </Dropdown.Content>
        </Dropdown>
    </>
}

Select.Trigger = SelectTrigger;
Select.Group = SelectGroup;
Select.Item = SelectItem;
Select.Option = SelectItem;